import {
  Table, Button, Badge, Modal, Text, Switch, Group,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useClientPaged } from '../hooks/useClientPaged';
import { TablePagination } from '../components/TablePagination';
import { IconPlus, IconPhoto, IconChevronRight } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { FetchOverlay } from '../components/FetchOverlay';
import { BannerPreview } from '../components/BannerPreview';
import { templateLabel } from '../lib/bannerDesign';
import { BannerBuilder, Banner, BannerPayload, audMeta, audienceLabel, KioskBadge } from '../components/BannerBuilder';
import { SectionCard } from '../components/SectionCard';

const apiErr = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

export default function Banners() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [opened, mh] = useDisclosure(false);

  const { data: banners = [], isLoading, isFetching } = useQuery({
    queryKey: ['banners'],
    queryFn: async () => (await api.get<Banner[]>('/banners')).data,
  });

  /* Ro'yxat to'liq olinadi (serverda sahifalash yo'q) — ko'rsatishni
     brauzerda bo'lamiz: uzun jadval sahifani sekinlashtiradi. */
  const sahifa = useClientPaged(banners, 'banners');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['banners'] });
  const audLabel = (v: string) => audienceLabel(t, v);

  const createMut = useMutation({
    mutationFn: (payload: BannerPayload) => api.post('/banners', payload),
    onSuccess: () => { notifications.show({ color: 'green', message: t('bannersPage.saved') }); invalidate(); mh.close(); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api.patch(`/banners/${id}`, { isActive }),
    onSuccess: invalidate,
  });

  return (
    <>
      <PageHeader title={t('bannersPage.title')} subtitle={t('bannersPage.subtitle')}
        action={<Button leftSection={<IconPlus size={16} />} onClick={mh.open}>{t('bannersPage.add')}</Button>} />

      <SectionCard title={t('common.list')} icon={IconPhoto} p={0} pos="relative">
        <FetchOverlay visible={isFetching && !isLoading} />
        {!isLoading && !banners.length ? (
          <EmptyState icon={IconPhoto} title={t('bannersPage.empty')} description={t('bannersPage.emptyDesc')} />
        ) : (
          <>
          <Table.ScrollContainer minWidth={720}>
            <Table verticalSpacing="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={230}>{t('bannersPage.preview')}</Table.Th>
                  <Table.Th>{t('bannersPage.templateTitle')}</Table.Th>
                  <Table.Th>{t('bannersPage.audience')}</Table.Th>
                  <Table.Th w={70}>{t('bannersPage.active')}</Table.Th>
                  <Table.Th w={40}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sahifa.items.map((b) => {
                  const m = audMeta(b.audience);
                  return (
                    <Table.Tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/banners/${b.id}`)}>
                      <Table.Td>
                        {/* Ilova kengligida (320px) chizib, zoom bilan kichiklashtiramiz —
                            nisbatlar aynan ilovadagidek qoladi, qisilib qolmaydi. */}
                        <div style={{ zoom: 0.58, width: 'fit-content' } as React.CSSProperties}>
                          <BannerPreview banner={b} theme="light" lang="uz" width={300} />
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6} mb={4}>
                          <Badge variant="light" color="grape">{templateLabel(t, b.template)}</Badge>
                          {/* Kiosk belgisi haqiqiy holatni ko'rsatadi (auditoriya toraytirilgan
                              bo'lsa TV'da chiqmaydi) — qarang: KioskBadge. */}
                          <KioskBadge banner={b} />
                        </Group>
                        <Text fw={600} size="sm">{b.title}</Text>
                      </Table.Td>
                      <Table.Td><Badge variant="light" color={m.color}>{audLabel(b.audience)}</Badge></Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Switch checked={b.isActive} onChange={(e) => toggleMut.mutate({ id: b.id, isActive: e.currentTarget.checked })} disabled={toggleMut.isPending} />
                      </Table.Td>
                      <Table.Td><IconChevronRight size={16} color="var(--mantine-color-dimmed)" /></Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <TablePagination
            page={sahifa.page}
            pages={sahifa.pages}
            total={sahifa.total}
            limit={sahifa.limit}
            onPage={sahifa.setPage}
            onLimit={sahifa.setLimit}
          />
          </>
        )}
      </SectionCard>

      {/* ── Yangi banner qo'shish ── */}
      <Modal opened={opened} onClose={mh.close} fullScreen title={t('bannersPage.createTitle')}>
        {opened && (
          <BannerBuilder
            onSubmit={(payload) => createMut.mutate(payload)}
            submitting={createMut.isPending}
            onCancel={mh.close}
          />
        )}
      </Modal>
    </>
  );
}
